import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal, Input, Select } from '@/components/common';
import {
  platformConfig,
  type DocumentType,
  type SecurityClassification,
  type DocumentStatus,
  type WorkflowTemplate,
} from '@/services/platform';
import styles from './PlatformConfigPage.module.css';

type TabType = 'document-types' | 'classifications' | 'statuses' | 'workflows';

// Map URL paths to tab keys
function getTabFromPath(pathname: string): TabType {
  if (pathname.includes('record-types') || pathname.includes('document-types')) {
    return 'document-types';
  }
  if (pathname.includes('classifications')) {
    return 'classifications';
  }
  if (pathname.includes('workflows')) {
    return 'workflows';
  }
  if (pathname.includes('statuses')) {
    return 'statuses';
  }
  return 'document-types'; // default
}

// Map tab keys to URL paths
function getPathFromTab(tab: TabType): string {
  switch (tab) {
    case 'document-types':
      return '/platform/config/record-types';
    case 'classifications':
      return '/platform/config/classifications';
    case 'workflows':
      return '/platform/config/workflows';
    case 'statuses':
      return '/platform/config';
    default:
      return '/platform/config';
  }
}

export function PlatformConfigPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>(() => getTabFromPath(location.pathname));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [classifications, setClassifications] = useState<SecurityClassification[]>([]);
  const [statuses, setStatuses] = useState<DocumentStatus[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowTemplate[]>([]);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Document Type form
  const [docTypeForm, setDocTypeForm] = useState({
    code: '',
    name: '',
    description: '',
    maxFileSizeMb: '50',
    requiresApproval: false,
  });

  // Classification form
  const [classForm, setClassForm] = useState({
    level: '1',
    code: '',
    name: '',
    description: '',
    color: '#6B7280',
  });

  // Status form
  const [statusForm, setStatusForm] = useState({
    code: '',
    name: '',
    description: '',
    color: '#6B7280',
    isFinal: false,
    allowEdit: true,
  });

  // Workflow form
  const [workflowForm, setWorkflowForm] = useState({
    code: '',
    name: '',
    description: '',
    category: 'general',
  });

  // Sync tab with URL path
  useEffect(() => {
    const tabFromPath = getTabFromPath(location.pathname);
    if (tabFromPath !== activeTab) {
      setActiveTab(tabFromPath);
    }
  }, [location.pathname]);

  // Update URL when tab changes (only if different from current path)
  function handleTabChange(tab: TabType) {
    setActiveTab(tab);
    const newPath = getPathFromTab(tab);
    if (location.pathname !== newPath) {
      navigate(newPath, { replace: true });
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    setError(null);

    try {
      const [typesRes, classRes, statusRes, workflowRes] = await Promise.all([
        platformConfig.getDocumentTypes(),
        platformConfig.getSecurityClassifications(),
        platformConfig.getDocumentStatuses(),
        platformConfig.getWorkflowTemplates(),
      ]);

      if (typesRes.success && typesRes.data) setDocumentTypes(typesRes.data);
      if (classRes.success && classRes.data) setClassifications(classRes.data);
      if (statusRes.success && statusRes.data) setStatuses(statusRes.data);
      if (workflowRes.success && workflowRes.data) setWorkflows(workflowRes.data);
    } catch {
      setError('Failed to load configuration data');
    } finally {
      setLoading(false);
    }
  }

  function openModal() {
    resetForms();
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
    resetForms();
  }

  function resetForms() {
    setDocTypeForm({ code: '', name: '', description: '', maxFileSizeMb: '50', requiresApproval: false });
    setClassForm({ level: '1', code: '', name: '', description: '', color: '#6B7280' });
    setStatusForm({ code: '', name: '', description: '', color: '#6B7280', isFinal: false, allowEdit: true });
    setWorkflowForm({ code: '', name: '', description: '', category: 'general' });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      let response;

      switch (activeTab) {
        case 'document-types':
          response = await platformConfig.createDocumentType({
            code: docTypeForm.code.toUpperCase(),
            name: docTypeForm.name,
            description: docTypeForm.description || undefined,
            maxFileSizeMb: parseInt(docTypeForm.maxFileSizeMb),
            requiresApproval: docTypeForm.requiresApproval,
          });
          break;

        case 'classifications':
          response = await platformConfig.createSecurityClassification({
            level: parseInt(classForm.level),
            code: classForm.code.toUpperCase(),
            name: classForm.name,
            description: classForm.description || undefined,
            color: classForm.color,
          });
          break;

        case 'statuses':
          response = await platformConfig.createDocumentStatus({
            code: statusForm.code.toUpperCase(),
            name: statusForm.name,
            description: statusForm.description || undefined,
            color: statusForm.color,
            isFinal: statusForm.isFinal,
            allowEdit: statusForm.allowEdit,
          });
          break;

        case 'workflows':
          response = await platformConfig.createWorkflowTemplate({
            code: workflowForm.code.toUpperCase(),
            name: workflowForm.name,
            description: workflowForm.description || undefined,
            category: workflowForm.category,
          });
          break;
      }

      if (response?.success) {
        await fetchData();
        closeModal();
      } else {
        setError('Failed to create item');
      }
    } catch {
      setError('Failed to create item');
    } finally {
      setSubmitting(false);
    }
  }

  const tabs: { key: TabType; label: string; count: number }[] = [
    { key: 'document-types', label: 'Record Types', count: documentTypes.length },
    { key: 'classifications', label: 'Security Classifications', count: classifications.length },
    { key: 'statuses', label: 'Document Statuses', count: statuses.length },
    { key: 'workflows', label: 'Workflow Templates', count: workflows.length },
  ];

  function getModalTitle(): string {
    switch (activeTab) {
      case 'document-types': return 'Add Record Type';
      case 'classifications': return 'Add Security Classification';
      case 'statuses': return 'Add Document Status';
      case 'workflows': return 'Add Workflow Template';
    }
  }

  function renderForm() {
    switch (activeTab) {
      case 'document-types':
        return (
          <>
            <Input
              label="Code"
              placeholder="e.g., MEMO"
              value={docTypeForm.code}
              onChange={(e) => setDocTypeForm({ ...docTypeForm, code: e.target.value.toUpperCase() })}
              required
            />
            <Input
              label="Name"
              placeholder="e.g., Memorandum"
              value={docTypeForm.name}
              onChange={(e) => setDocTypeForm({ ...docTypeForm, name: e.target.value })}
              required
            />
            <Input
              label="Description"
              placeholder="Brief description..."
              value={docTypeForm.description}
              onChange={(e) => setDocTypeForm({ ...docTypeForm, description: e.target.value })}
            />
            <Input
              label="Max File Size (MB)"
              type="number"
              value={docTypeForm.maxFileSizeMb}
              onChange={(e) => setDocTypeForm({ ...docTypeForm, maxFileSizeMb: e.target.value })}
            />
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={docTypeForm.requiresApproval}
                onChange={(e) => setDocTypeForm({ ...docTypeForm, requiresApproval: e.target.checked })}
              />
              <span>Requires Approval</span>
            </label>
          </>
        );

      case 'classifications':
        return (
          <>
            <Input
              label="Level (1-5)"
              type="number"
              min="1"
              max="5"
              value={classForm.level}
              onChange={(e) => setClassForm({ ...classForm, level: e.target.value })}
              required
            />
            <Input
              label="Code"
              placeholder="e.g., CONFIDENTIAL"
              value={classForm.code}
              onChange={(e) => setClassForm({ ...classForm, code: e.target.value.toUpperCase() })}
              required
            />
            <Input
              label="Name"
              placeholder="e.g., Confidential"
              value={classForm.name}
              onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
              required
            />
            <Input
              label="Description"
              placeholder="Brief description..."
              value={classForm.description}
              onChange={(e) => setClassForm({ ...classForm, description: e.target.value })}
            />
            <div className={styles.colorField}>
              <label>Color</label>
              <input
                type="color"
                value={classForm.color}
                onChange={(e) => setClassForm({ ...classForm, color: e.target.value })}
              />
            </div>
          </>
        );

      case 'statuses':
        return (
          <>
            <Input
              label="Code"
              placeholder="e.g., APPROVED"
              value={statusForm.code}
              onChange={(e) => setStatusForm({ ...statusForm, code: e.target.value.toUpperCase() })}
              required
            />
            <Input
              label="Name"
              placeholder="e.g., Approved"
              value={statusForm.name}
              onChange={(e) => setStatusForm({ ...statusForm, name: e.target.value })}
              required
            />
            <Input
              label="Description"
              placeholder="Brief description..."
              value={statusForm.description}
              onChange={(e) => setStatusForm({ ...statusForm, description: e.target.value })}
            />
            <div className={styles.colorField}>
              <label>Color</label>
              <input
                type="color"
                value={statusForm.color}
                onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })}
              />
            </div>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={statusForm.isFinal}
                onChange={(e) => setStatusForm({ ...statusForm, isFinal: e.target.checked })}
              />
              <span>Is Final Status</span>
            </label>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={statusForm.allowEdit}
                onChange={(e) => setStatusForm({ ...statusForm, allowEdit: e.target.checked })}
              />
              <span>Allow Edit</span>
            </label>
          </>
        );

      case 'workflows':
        return (
          <>
            <Input
              label="Code"
              placeholder="e.g., DOC_APPROVAL"
              value={workflowForm.code}
              onChange={(e) => setWorkflowForm({ ...workflowForm, code: e.target.value.toUpperCase() })}
              required
            />
            <Input
              label="Name"
              placeholder="e.g., Document Approval"
              value={workflowForm.name}
              onChange={(e) => setWorkflowForm({ ...workflowForm, name: e.target.value })}
              required
            />
            <Input
              label="Description"
              placeholder="Brief description..."
              value={workflowForm.description}
              onChange={(e) => setWorkflowForm({ ...workflowForm, description: e.target.value })}
            />
            <Select
              label="Category"
              value={workflowForm.category}
              onChange={(e) => setWorkflowForm({ ...workflowForm, category: e.target.value })}
            >
              <option value="general">General</option>
              <option value="document">Document</option>
              <option value="approval">Approval</option>
              <option value="correspondence">Correspondence</option>
              <option value="personnel">Personnel</option>
            </Select>
          </>
        );
    }
  }

  function renderContent() {
    if (loading) {
      return <div className={styles.loading}>Loading...</div>;
    }

    switch (activeTab) {
      case 'document-types':
        return documentTypes.length === 0 ? (
          <div className={styles.emptyState}>No document types configured</div>
        ) : (
          <div className={styles.grid}>
            {documentTypes.map((item) => (
              <div key={item.id} className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.code}>{item.code}</span>
                  <span className={`${styles.badge} ${item.isActive ? styles.active : styles.inactive}`}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <h3 className={styles.cardName}>{item.name}</h3>
                {item.description && <p className={styles.cardDesc}>{item.description}</p>}
                <div className={styles.cardMeta}>
                  <span>Max: {item.maxFileSizeMb}MB</span>
                  {item.requiresApproval && <span className={styles.tag}>Requires Approval</span>}
                </div>
              </div>
            ))}
          </div>
        );

      case 'classifications':
        return classifications.length === 0 ? (
          <div className={styles.emptyState}>No security classifications configured</div>
        ) : (
          <div className={styles.grid}>
            {classifications.map((item) => (
              <div key={item.id} className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.levelBadge} style={{ backgroundColor: item.color }}>
                    Level {item.level}
                  </span>
                </div>
                <h3 className={styles.cardName}>{item.name}</h3>
                <span className={styles.code}>{item.code}</span>
                {item.description && <p className={styles.cardDesc}>{item.description}</p>}
              </div>
            ))}
          </div>
        );

      case 'statuses':
        return statuses.length === 0 ? (
          <div className={styles.emptyState}>No document statuses configured</div>
        ) : (
          <div className={styles.grid}>
            {statuses.map((item) => (
              <div key={item.id} className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.colorDot} style={{ backgroundColor: item.color }} />
                  <span className={styles.code}>{item.code}</span>
                </div>
                <h3 className={styles.cardName}>{item.name}</h3>
                {item.description && <p className={styles.cardDesc}>{item.description}</p>}
                <div className={styles.cardMeta}>
                  {item.isFinal && <span className={styles.tag}>Final</span>}
                  {item.allowEdit && <span className={styles.tag}>Editable</span>}
                </div>
              </div>
            ))}
          </div>
        );

      case 'workflows':
        return workflows.length === 0 ? (
          <div className={styles.emptyState}>No workflow templates configured</div>
        ) : (
          <div className={styles.grid}>
            {workflows.map((item) => (
              <div key={item.id} className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.code}>{item.code}</span>
                  <span className={`${styles.badge} ${item.isActive ? styles.active : styles.inactive}`}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <h3 className={styles.cardName}>{item.name}</h3>
                {item.description && <p className={styles.cardDesc}>{item.description}</p>}
                <div className={styles.cardMeta}>
                  <span className={styles.tag}>{item.category}</span>
                </div>
              </div>
            ))}
          </div>
        );
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Platform Configuration</h1>
          <p className={styles.subtitle}>Manage global document types, classifications, and workflows</p>
        </div>
        <Button onClick={openModal}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Add New
        </Button>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button onClick={() => setError(null)} className={styles.errorClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}

      <div className={styles.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.activeTab : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
            <span className={styles.tabCount}>{tab.count}</span>
          </button>
        ))}
      </div>

      <div className={styles.content}>
        {renderContent()}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={getModalTitle()}
      >
        <form className={styles.form} onSubmit={handleSubmit}>
          {renderForm()}
          <div className={styles.modalActions}>
            <Button variant="secondary" type="button" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
